import React, { useEffect } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Common, SettingsIcons } from "../../assets/images";
// Redux imports
import { TransferEmployee } from "../../interfaces/letters";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setFilterTransferEmployeeTotalPages,
  setFilterNewJoinerTotalPages,
  setTransferEmployeeLoading,
  setNewJoinersLoading,
} from "../../store/slices/lettersSlice";
// API imports
import { useGetAllFilteredTransferNewJoinersMutation } from "../../services/Letters";
import SelectSearch from "react-select-search";

interface Props {
  show?: boolean;
  setShow?: any;
  setTransferRequests?: any;
  filterSearch: boolean;
  setFilterSearch: any;
  transferRequests: TransferEmployee[];
  query: string;
  filterInput: FormProps;
  setFilterInput: any;
  newJoiner: boolean;
}

export interface FormProps {
  projectId: string;
  projectSupervisor: string;
  newProjectId: string;
  transferStatus: string;
  transferCategory: string;
}

const TransferRequestFilterModal = ({
  show,
  setShow,
  transferRequests,
  setTransferRequests,
  setFilterSearch,
  query,
  filterInput,
  setFilterInput,
  filterSearch,
  newJoiner,
}: Props) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.letters);
  const accessToken = useAppSelector((state) => state.common.accessToken);
  const userID = useAppSelector((state) => state.common.userID);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const allUsers = useAppSelector((state) => state.settings.allUsers);
  const projectIds = useAppSelector((state) => state.project.projectIds);
  const [getAllFilteredTransferNewJoiners] =
    useGetAllFilteredTransferNewJoinersMutation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    newJoiner
      ? dispatch(setNewJoinersLoading(true))
      : dispatch(setTransferEmployeeLoading(true));
    setFilterSearch(true);
    setShow(false);
    // Filter employees based on the form values
    try {
      const data = {
        userID: userID || "",
        accessToken: accessToken || "",
        permission: profileData?.permissions.showEmployee || false,
        payload: {
          page: newJoiner
            ? state.filterNewJoinerPageNo
            : state.filterTransferEmployeePageNo,
          limit: newJoiner
            ? state.filterNewJoinerPageSize
            : state.filterTransferEmployeePageSize,
          projectId: filterInput.projectId,
          projectSupervisor: filterInput.projectSupervisor,
          newProjectId: filterInput.newProjectId,
          transferStatus: filterInput.transferStatus,
          transferCategory: filterInput.transferCategory,
        },
      };
      const response = await getAllFilteredTransferNewJoiners(data).unwrap();
      if (response) {
        setTransferRequests(response.data.joiners[0].data);
        newJoiner
          ? dispatch(
              setFilterNewJoinerTotalPages(
                response.data.joiners[0].metadata[0].total,
              ),
            )
          : dispatch(
              setFilterTransferEmployeeTotalPages(
                response.data.joiners[0].metadata[0].total,
              ),
            );
      }
    } catch (e: any) {
    } finally {
      newJoiner
        ? dispatch(setNewJoinersLoading(true))
        : dispatch(setTransferEmployeeLoading(true));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      newJoiner
        ? dispatch(setNewJoinersLoading(true))
        : dispatch(setTransferEmployeeLoading(true));
      // Filter employees based on the form values
      try {
        const data = {
          userID: userID || "",
          accessToken: accessToken || "",
          permission: profileData?.permissions.showEmployee || false,
          payload: {
            page: newJoiner
              ? state.filterNewJoinerPageNo
              : state.filterTransferEmployeePageNo,
            limit: newJoiner
              ? state.filterNewJoinerPageSize
              : state.filterTransferEmployeePageSize,
            empId: query,
            projectId: filterInput.projectId,
            projectSupervisor: filterInput.projectSupervisor,
            newProjectId: filterInput.newProjectId,
            transferStatus: filterInput.transferStatus,
            transferCategory: filterInput.transferCategory,
          },
        };
        const response = await getAllFilteredTransferNewJoiners(data).unwrap();
        if (response) {
          setTransferRequests(response.data.joiners[0].data);
          newJoiner
            ? dispatch(
                setFilterNewJoinerTotalPages(
                  response.data.joiners[0].metadata[0].total,
                ),
              )
            : dispatch(
                setFilterTransferEmployeeTotalPages(
                  response.data.joiners[0].metadata[0].total,
                ),
              );
        }
      } catch (e: any) {
      } finally {
        newJoiner
          ? dispatch(setNewJoinersLoading(true))
          : dispatch(setTransferEmployeeLoading(true));
      }
    };
    if (filterSearch) {
      fetchData();
    }
  }, [
    newJoiner
      ? state.filterNewJoinerPageNo
      : state.filterTransferEmployeePageNo,
    newJoiner
      ? state.filterNewJoinerPageSize
      : state.filterTransferEmployeePageSize,
    query,
  ]);

  const handleReset = () => {
    setTransferRequests(transferRequests);
    setFilterInput({
      projectId: "",
      projectSupervisor: "",
      newProjectId: "",
      transferStatus: "",
      transferCategory: "",
    });
  };

  // Options
  const supervisors = allUsers.filter((item) => item.role === "Supervisor");
  const supervisorOptions = supervisors.map((employee) => ({
    name: employee.firstName
      ? employee.firstName + " " + employee.lastName
      : "Not Signed Up", // Use the actual property name for the project name
    value: employee.employeeId,
  }));

  const projectOptions = projectIds.map((employee) => ({
    name: employee.projectId + " - " + employee.projectName, // Use the actual property name for the project name
    value: employee.projectId,
  }));

  return (
    <>
      <Modal
        show={show}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="lg"
        className="general-modal"
      >
        <Modal.Body className="text-center px-5">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="heading">
              {newJoiner
                ? "Filter New Joiners Requests"
                : "Filter Transfer Employee Requests"}
            </h4>
            <button className="btn p-0 border-0" onClick={() => setShow(false)}>
              <img src={SettingsIcons.CrossIcon} alt="edit-icon" />
            </button>
          </div>
          <div className="change-password">
            <Form onSubmit={handleSubmit} className="text-end">
              <Row>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Old Project Id
                    </h4>
                    <Form.Group className="position-relative input-design d-flex align-items-center">
                      <SelectSearch
                        name="projectId"
                        options={projectOptions}
                        value={filterInput.projectId}
                        onChange={(value: any) => {
                          setFilterInput({
                            ...filterInput,
                            projectId: value,
                          });
                        }}
                        search={true}
                        placeholder="Select Project Id."
                      />
                      <img src={Common.ArrowDown} alt="message" />
                    </Form.Group>
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Project Supervisor
                    </h4>
                    <Form.Group className="position-relative input-design d-flex align-items-center">
                      <SelectSearch
                        name="projectSupervisor"
                        options={supervisorOptions}
                        value={filterInput.projectSupervisor}
                        onChange={(value: any) => {
                          setFilterInput({
                            ...filterInput,
                            projectSupervisor: value,
                          });
                        }}
                        search={true}
                        placeholder="Select Project Supervisor"
                      />
                      <img src={Common.ArrowDown} alt="message" />
                    </Form.Group>
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      New Project Id
                    </h4>
                    <Form.Group className="position-relative input-design d-flex align-items-center">
                      <SelectSearch
                        name="newProjectId"
                        options={projectOptions}
                        value={filterInput.newProjectId}
                        onChange={(value: any) => {
                          setFilterInput({
                            ...filterInput,
                            newProjectId: value,
                          });
                        }}
                        search={true}
                        placeholder="Select New Project Id."
                      />
                      <img src={Common.ArrowDown} alt="message" />
                    </Form.Group>
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Request Status
                    </h4>
                    <Form.Select
                      name="transferStatus"
                      onChange={(e) =>
                        setFilterInput({
                          ...filterInput,
                          transferStatus: e.target.value,
                        })
                      }
                      value={filterInput.transferStatus}
                      className="custom-input border-fill"
                    >
                      <option value="">Select Status</option>
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <div className="mt-4">
                <button
                  className="btn primary-button text-center w-25 me-2"
                  onClick={handleReset}
                  type="button"
                >
                  Reset
                </button>
                <button
                  className="btn primary-button text-center w-25"
                  type="submit"
                >
                  Filter
                </button>
              </div>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TransferRequestFilterModal;
